/**
 * ============================================================================
 * TEMPLATES STORE - FETCH OPERATIONS
 * ============================================================================
 * 
 * Handles fetching templates from the backend API and syncing with local store.
 * 
 * Data Flow:
 * 1. Request templates from API
 * 2. API returns array of GlossaryBlueprint
 * 3. Store syncs with fetched data
 * 4. Returns fetched items
 * 
 * @module templates/api/fetch
 */

import { apiService } from "@/lib/api-functions/api-service";
import { templatesStore } from "../store";
import type { GlossaryBlueprint } from "@/types-games";
import type { ToastFunction } from "../types";

// ============================================================================
// FETCH OPERATIONS
// ============================================================================

/**
 * Fetch templates for a specific game from the API and sync the store.
 * 
 * @param gameId - The game ID to fetch templates for
 * @param folder - Optional folder ID to filter items by folder (not currently used by API)
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to array of fetched templates
 * 
 * @example
 * ```ts
 * // Fetch all templates for a game
 * const templates = await fetchTemplates(gameId);
 * ```
 */
export async function fetchTemplates(
  gameId: string,
  folder?: string | null,
  toast?: ToastFunction
): Promise<GlossaryBlueprint[]> {
  try {
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch data from API
    const result = await apiService.fetchBlueprints(gameId);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch templates');
    }

    const templates = result.data;
    
    // Sync the store with the fetched data
    await templatesStore.syncFromRemote(async () => templates);
    
    return templates;
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch templates';
    console.error(`[fetchTemplates] Error for gameId: ${gameId}:`, err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
